import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Form, ListGroup } from "react-bootstrap";

const ChipAutocomplete = ({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = "Escriba para buscar...",
  disabled = false,
  emptyMessage = "Sin coincidencias",
}) => {
  const [input, setInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);

  const selected = Array.isArray(value) ? value : [];

  const filteredOptions = useMemo(() => {
    const query = input.trim().toLowerCase();
    const notSelected = options.filter((opt) => !selected.includes(opt));
    if (!query) return notSelected;
    return notSelected.filter((opt) => opt.toLowerCase().includes(query));
  }, [input, options, selected]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addChip = (opt) => {
    if (!opt || selected.includes(opt)) return;
    onChange([...selected, opt]);
    setInput("");
    setHighlightIndex(0);
  };

  const removeChip = (opt) => {
    onChange(selected.filter((s) => s !== opt));
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setShowSuggestions(true);
      setHighlightIndex((i) => Math.min(i + 1, Math.max(filteredOptions.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (showSuggestions && filteredOptions[highlightIndex]) {
        addChip(filteredOptions[highlightIndex]);
      }
    } else if (event.key === "Backspace" && !input && selected.length > 0) {
      removeChip(selected[selected.length - 1]);
    } else if (event.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  return (
    <Form.Group ref={containerRef} className="position-relative">
      {label && <Form.Label>{label}</Form.Label>}

      <div
        className="form-control d-flex flex-wrap align-items-center"
        style={{ minHeight: "38px", height: "auto", padding: "4px 8px", cursor: "text" }}
        onClick={() => {
          if (!disabled) setShowSuggestions(true);
        }}
      >
        {selected.map((opt) => (
          <Badge
            key={opt}
            variant="primary"
            className="mr-1 my-1 d-inline-flex align-items-center"
            style={{ fontSize: "13px", padding: "6px 8px" }}
          >
            {opt}
            <span
              role="button"
              aria-label={`Remover ${opt}`}
              onClick={(e) => {
                e.stopPropagation();
                removeChip(opt);
              }}
              style={{ marginLeft: "6px", cursor: "pointer", fontWeight: "bold" }}
            >
              ×
            </span>
          </Badge>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setShowSuggestions(true);
            setHighlightIndex(0);
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={selected.length === 0 ? placeholder : ""}
          className="border-0 flex-grow-1"
          style={{ outline: "none", minWidth: "120px", background: "transparent" }}
        />
      </div>

      {showSuggestions && !disabled && (
        <ListGroup
          className="position-absolute w-100 shadow-sm"
          style={{ zIndex: 1000, maxHeight: "220px", overflowY: "auto" }}
        >
          {filteredOptions.length === 0 ? (
            <ListGroup.Item disabled className="text-muted">
              {emptyMessage}
            </ListGroup.Item>
          ) : (
            filteredOptions.map((opt, idx) => (
              <ListGroup.Item
                key={opt}
                action
                active={idx === highlightIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  addChip(opt);
                }}
                onMouseEnter={() => setHighlightIndex(idx)}
              >
                {opt}
              </ListGroup.Item>
            ))
          )}
        </ListGroup>
      )}
    </Form.Group>
  );
};

export default ChipAutocomplete;
